import * as React from 'react';

import {Stack} from 'shells/layout/stack/stack';
import {PopoverInfo} from 'shells/popover-info/popover-info';
import {Radio} from 'shells/radio/radio';
import {RadioGroup} from 'shells/radio/radio-group';
import {useManageSaveState} from 'shells/saved-setting-toast/use-manage-save-state';
import {Body} from 'shells/typography/body';
import {LabelWithContent} from 'shells/typography/label-with-content';

import type {ExternalServiceType} from 'nutshell-graphql-types';
import type {GetSessionUser_loggedInUser as User} from 'nutshell-graphql-types';

import {useUpdateUserSetting} from '../core/use-update-user-setting';

import {
    getUserAutoLogSettingName,
    getServiceNoun,
    getServiceAutoLogSettingName,
    isUserAutoLogEnabled,
    getLogModes,
} from './helpers';
import {LOG_MODE_AUTOLOG, LOG_MODE_MANUAL} from './tables/consts';
import type {LogMode} from './tables/consts';

type Props = {
    serviceType: ExternalServiceType;
    sendAutologValueToParent?: (arg1: string) => void;
    user: User;
};

export function TranscriptionModePicker(props: Props) {
    // Need a unique name prop for the radio group
    // Can have multiple transcription mode pickers for the same service type rendered at once
    // So using the service type is not enough
    const instanceIdRef = React.useRef(
        `transcription-mode-${Math.random().toString(36).substr(2, 9)}`
    );

    const [isSavingMeetingOptions, setIsSavingMeetingOptions] = React.useState<boolean>(false);

    const {toastState} = useManageSaveState(isSavingMeetingOptions, 1000);
    const updateUserSetting = useUpdateUserSetting();

    const userAutoLogSettingName = getUserAutoLogSettingName(props.serviceType, props.user);
    const autoLogSettingName = getServiceAutoLogSettingName(props.serviceType);
    const hasEnabledAutoLog = isUserAutoLogEnabled(props.serviceType, props.user);

    const handleLogModeChange = (value: LogMode) => {
        if (!userAutoLogSettingName || !autoLogSettingName) {
            return;
        }
        setIsSavingMeetingOptions(true);

        updateUserSetting({
            userId: props.user.id,
            settingName: autoLogSettingName,
            value: value === LOG_MODE_AUTOLOG ? '1' : '0',
            propertyOnUser: userAutoLogSettingName,
        }).then(() => {
            if (props.sendAutologValueToParent) {
                props.sendAutologValueToParent(value === LOG_MODE_AUTOLOG ? '1' : '0');
            }
            setIsSavingMeetingOptions(false);
        });
    };

    return (
        <LabelWithContent
            label={`How do you want to log ${getServiceNoun(props.serviceType)} calls?`}
            spacing={8}
            state={toastState}
            content={
                <RadioGroup
                    name={instanceIdRef.current}
                    selectedValue={hasEnabledAutoLog ? LOG_MODE_AUTOLOG : LOG_MODE_MANUAL}
                    // @ts-expect-error
                    onChange={handleLogModeChange}
                >
                    {getLogModes(props.serviceType).map((mode) => (
                        <Radio
                            key={mode.value}
                            value={mode.value}
                            shouldWidthFitContent={true}
                            shouldTopAlignInput={Boolean(mode.sublabel)}
                            // @ts-expect-error
                            tabIndex={0}
                        >
                            <Stack>
                                <Stack isHorizontal={true} spacing={4}>
                                    {mode.label}
                                    {mode.tooltipInfo && (
                                        <PopoverInfo
                                            size={16}
                                            info={mode.tooltipInfo}
                                            noIconPadding={true}
                                        />
                                    )}
                                </Stack>
                                {mode.sublabel && (
                                    <Body size='small' isItalic={true} color='light'>
                                        {mode.sublabel}
                                    </Body>
                                )}
                            </Stack>
                        </Radio>
                    ))}
                </RadioGroup>
            }
        />
    );
}
